import React, { useState, useEffect } from 'react';
import apiClient from '../api/apiClient';
import Layout from '../components/layout/Layout';
import { FaTrash, FaEdit, FaPlus, FaUsersCog } from 'react-icons/fa';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { usePermissions } from '../hooks/usePermissions';
import { ReadOnlyIndicator } from '../components/ui/PermissionWrapper';

const HumanAgents = () => {
  const { projectId } = useParams();
  const permissions = usePermissions();
  const [isLoading, setIsLoading] = useState(false);
  const [project, setProject] = useState(null);
  const [agents, setAgents] = useState([]);
  const [newAgent, setNewAgent] = useState({
    name: '',
    email: '',
    password: '',
    additionalInfo: ''
  });
  const [settings, setSettings] = useState({
    enabled: false,
    transferMessage: '',
    waitMessage: '',
    noAgentsMessage: ''
  });
  const [transfers, setTransfers] = useState([]);
  const [transferHistory, setTransferHistory] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Check permissions
  const canManageHumanAgents = permissions.canManage('humanAgents');
  const canViewHumanAgents = permissions.canView('humanAgents');
  const hasReadOnlyAccess = canViewHumanAgents && !canManageHumanAgents;

  useEffect(() => {
    fetchProjectDetails();
    fetchAgents();
    fetchTransfers();
    fetchTransferHistory();
  }, [projectId]);


  const fetchProjectDetails = async () => {
    try {
      const response = await apiClient.get(`/projects/${projectId}`);
      setProject(response.data.project);
      
      // Initialize settings from project data
      if (response.data.project.humanAgents) {
        setSettings({
          enabled: response.data.project.humanAgents.enabled || false,
          transferMessage: response.data.project.humanAgents.transferMessage || '',
          waitMessage: response.data.project.humanAgents.waitMessage || '',
          noAgentsMessage: response.data.project.humanAgents.noAgentsMessage || ''
        });
      }
    } catch (error) {
      toast.error(error.message || 'Error fetching project details');
    }
  };

  const fetchAgents = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.get(`/projects/${projectId}/human-agents`);
      setAgents(response.data.humanAgents);
    } catch (error) {
      toast.error(error.message || 'Error fetching agents');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTransfers = async () => {
    try {
      const response = await apiClient.get(`/projects/${projectId}/transfers`);
      setTransfers(response.data.transfers);
    } catch (error) {
      toast.error(error.message || 'Error fetching transfers');
    }
  };

  const fetchTransferHistory = async () => {
    try {
      const response = await apiClient.get(`/projects/${projectId}/transfer-history`);
      setTransferHistory(response.data.transfers);
    } catch (error) {
      toast.error(error.message || 'Error fetching transfer history');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewAgent({ ...newAgent, [name]: value });
  };

  const handleSettingsChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings({
      ...settings,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const saveSettings = async () => {
    try {
      setIsLoading(true);
      await apiClient.patch(`/projects/${projectId}/human-agents-settings`, settings);
      toast.success('Human agent settings have been updated');
      fetchProjectDetails();
    } catch (error) {
      toast.error(error.message || 'Error updating settings');
    } finally {
      setIsLoading(false);
    }
  };

  const addAgent = async () => {
    try {
      setIsLoading(true);
      
      // Validate inputs
      if (!newAgent.name || !newAgent.email || !newAgent.password) {
        toast.error('Please fill in all fields');
        return;
      }
      
      // Validate password length
      if (newAgent.password.length < 8) {
        toast.error('Password must be at least 8 characters long');
        return;
      }
      
      await apiClient.post(`/projects/${projectId}/human-agents`, newAgent);
      
      toast.success('Human agent has been added');
      
      // Clear form and refresh list
      setNewAgent({
        name: '',
        email: '',
        password: '',
        additionalInfo: ''
      });
      setIsModalOpen(false);
      fetchAgents();
    } catch (error) {
      toast.error(error.message || 'Error adding agent');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteAgent = async (agentId) => {
    try {
      setIsLoading(true);
      await apiClient.delete(`/projects/${projectId}/human-agents/${agentId}`);
      
      toast.success('Human agent has been removed');
      
      fetchAgents();
    } catch (error) {
      toast.error(error.message || 'Error removing agent');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <div className="p-5">
        <h1 className="text-2xl font-bold mb-6">Human Agent Management</h1>
        
        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('settings')}
                className={`tabButton ${
                  activeTab === 'settings'
                    ? 'border-blue-500 text-blue-600 selectedTab'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Settings
              </button>
              <button
                onClick={() => setActiveTab('agents')}
                className={`tabButton ${
                  activeTab === 'agents'
                    ? 'border-blue-500 text-blue-600 selectedTab'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Agents
              </button>
              <button
                onClick={() => setActiveTab('transfers')}
                className={`tabButton ${
                  activeTab === 'transfers'
                    ? 'border-blue-500 text-blue-600 selectedTab'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Active Transfers
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`tabButton ${
                  activeTab === 'history'
                    ? 'border-blue-500 text-blue-600 selectedTab'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Transfer History
              </button>
            </nav>
          </div>
        </div>

        {/* Settings Panel */}
        {activeTab === 'settings' && (
          <div className="bg-white shadow-md rounded-lg mb-6 overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-medium">Human Agent Settings</h2>
              {!hasReadOnlyAccess && (
                <button 
                  className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded flex items-center btn btn-primary text-smd"
                  onClick={saveSettings}
                  disabled={isLoading}
                >
                  {isLoading ? 'Saving...' : 'Save Settings'}
                </button>
              )}
            </div>
            <div className="p-4">
              <div className="flex items-center mb-4">
                <label htmlFor="enabled" className="mr-2 font-medium">
                  Enable Human Agents
                </label>
                <div className="relative inline-block w-12 mr-2 align-middle select-none">
                  <input
                    type="checkbox"
                    name="enabled"
                    id="enabled"
                    checked={settings.enabled}
                    onChange={handleSettingsChange}
                    disabled={hasReadOnlyAccess}
                    className={`${
                      settings.enabled ? 'bg-blue-500' : 'bg-gray-300'
                    } absolute block w-6 h-6 rounded-full border-4 appearance-none cursor-pointer`}
                    style={{
                      transform: settings.enabled ? 'translateX(100%)' : 'translateX(0)',
                      transition: 'transform 0.3s ease-in-out'
                    }}
                  />
                  <label
                    htmlFor="enabled"
                    className="block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer"
                  />
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block font-medium mb-2">Transfer Message</label>
                <textarea
                  name="transferMessage"
                  value={settings.transferMessage}
                  onChange={handleSettingsChange}
                  placeholder="Message shown when transferring to a human agent"
                  className="w-full px-3 py-2 border rounded-lg text-smd pl-2 pr-2"
                  rows="3"
                  disabled={hasReadOnlyAccess}
                />
                <p className="text-sm text-gray-500 mt-1">
                  Shown when a transfer to a human agent is initiated
                </p>
              </div>
              
              <div className="mb-4">
                <label className="block font-medium mb-2">Wait Message</label>
                <textarea
                  name="waitMessage"
                  value={settings.waitMessage}
                  onChange={handleSettingsChange}
                  placeholder="Message shown while waiting for an agent"
                  className="w-full px-3 py-2 border rounded-lg text-smd pl-2 pr-2"
                  rows="3"
                  disabled={hasReadOnlyAccess}
                />
                <p className="text-sm text-gray-500 mt-1">
                  Shown while the user is waiting for an agent to accept the transfer
                </p>
              </div>
              
              <div className="mb-4">
                <label className="block font-medium mb-2">No Agents Message</label>
                <textarea
                  name="noAgentsMessage"
                  value={settings.noAgentsMessage}
                  onChange={handleSettingsChange}
                  placeholder="Message shown when no agents are available"
                  className="w-full px-3 py-2 border rounded-lg text-smd pl-2 pr-2"
                  rows="3"
                  disabled={hasReadOnlyAccess}
                />
                <p className="text-sm text-gray-500 mt-1">
                  Shown when there are no human agents available
                </p>
              </div>

              {hasReadOnlyAccess && (
                <ReadOnlyIndicator />
              )}
            </div>
          </div>
        )}

        {/* Agents Panel */}
        {activeTab === 'agents' && (
          <div className="bg-white shadow-md rounded-lg mb-6 overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-medium">Human Agents</h2>
              {!hasReadOnlyAccess && (
                <button 
                  className="flex items-center bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-smd btn btn-secondary"
                  onClick={() => setIsModalOpen(true)}
                  disabled={!settings.enabled}
                >
                  <FaPlus className="mr-2" /> Add Agent
                </button>
              )}
            </div>
            <div className="p-4">
              {!settings.enabled ? (
                <p className="text-red-500">Enable Human Agents in the settings tab to manage agents.</p>
              ) : agents.length === 0 ? (
                <p>No human agents found. Click "Add Agent" to create one.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Additional Info</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Active</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {agents.map((agent) => (
                        <tr key={agent._id}>
                          <td className="px-6 py-4 whitespace-nowrap">{agent.name}</td>
                          <td className="px-6 py-4 whitespace-nowrap">{agent.email}</td>
                          <td className="px-6 py-4">{agent.additionalInfo || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                                ${agent.status === 'online' ? 'bg-green-100 text-green-800' : 
                                  agent.status === 'busy' ? 'bg-yellow-100 text-yellow-800' : 
                                  'bg-gray-100 text-gray-800'}`}
                            >
                              {agent.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {agent.lastActive
                              ? new Date(agent.lastActive).toLocaleString()
                              : 'Never'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {!hasReadOnlyAccess && (
                              <button
                                className="flex items-center bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm"
                                onClick={() => deleteAgent(agent._id)}
                              >
                                <FaTrash className="mr-1" /> Remove
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Active Transfers Panel */}
        {activeTab === 'transfers' && (
          <div className="bg-white shadow-md rounded-lg mb-6 overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-medium">Active Transfers</h2>
            </div>
            <div className="p-4">
              {transfers.length === 0 ? (
                <p>No active transfers at the moment.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Agent</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Wait Time</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {transfers.map((transfer) => (
                        <tr key={transfer._id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                                ${transfer.status === 'active' ? 'bg-green-100 text-green-800' : 
                                  transfer.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                                  'bg-blue-100 text-blue-800'}`}
                            >
                              {transfer.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">{transfer.chatSession?.userName || 'Unknown'}</td>
                          <td className="px-6 py-4 whitespace-nowrap">{transfer.humanAgent?.name || 'Not assigned'}</td>
                          <td className="px-6 py-4 whitespace-nowrap">{new Date(transfer.createdAt).toLocaleString()}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {transfer.userWaitTime
                              ? `${transfer.userWaitTime}s`
                              : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">{transfer.reason || 'Not specified'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Transfer History Panel */}
        {activeTab === 'history' && (
          <div className="bg-white shadow-md rounded-lg mb-6 overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-medium">Transfer History</h2>
            </div>
            <div className="p-4">
              {transferHistory.length === 0 ? (
                <p>No transfer history available.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Agent</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Completed</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Wait Time</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {transferHistory.map((transfer) => (
                        <tr key={transfer._id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                                ${transfer.status === 'completed' ? 'bg-green-100 text-green-800' : 
                                  transfer.status === 'cancelled' ? 'bg-red-100 text-red-800' : 
                                  'bg-orange-100 text-orange-800'}`}
                            >
                              {transfer.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">{transfer.chatSession?.userName || 'Unknown'}</td>
                          <td className="px-6 py-4 whitespace-nowrap">{transfer.humanAgent?.name || 'None'}</td>
                          <td className="px-6 py-4 whitespace-nowrap">{new Date(transfer.createdAt).toLocaleString()}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {transfer.completedTime
                              ? new Date(transfer.completedTime).toLocaleString()
                              : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {transfer.userWaitTime
                              ? `${transfer.userWaitTime}s`
                              : 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Add Agent Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-lg font-medium">Add Human Agent</h3>
                <button 
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-4">
                <div className="mb-4">
                  <label className="block font-medium mb-2">Name</label>
                  <input
                    name="name"
                    value={newAgent.name}
                    onChange={handleInputChange}
                    placeholder="Agent's name"
                    className="w-full px-3 py-2 border rounded-lg text-smd pl-2 pr-2"
                  />
                </div>
                <div className="mb-4">
                  <label className="block font-medium mb-2">Email</label>
                  <input
                    name="email"
                    type="email"
                    value={newAgent.email}
                    onChange={handleInputChange}
                    placeholder="agent@example.com"
                    className="w-full px-3 py-2 border rounded-lg text-smd pl-2 pr-2"
                  />
                </div>
                <div className="mb-4">
                  <label className="block font-medium mb-2">Password</label>
                  <input
                    name="password"
                    type="password"
                    value={newAgent.password}
                    onChange={handleInputChange}
                    placeholder="Minimum 8 characters"
                    className="w-full px-3 py-2 border rounded-lg text-smd pl-2 pr-2"
                  />
                </div>
                <div className="mb-4">
                  <label className="block font-medium mb-2">Additional Info</label>
                  <textarea
                    name="additionalInfo"
                    value={newAgent.additionalInfo}
                    onChange={handleInputChange}
                    placeholder="Additional information about the agent (location, expertise, etc.)"
                    className="w-full px-3 py-2 border rounded-lg text-smd pl-2 pr-2"
                    rows="3"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    This information will be used to match the agent with appropriate customer issues
                  </p>
                </div>
              </div>
              <div className="p-4 border-t border-gray-200 flex justify-end">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded mr-2 text-smd btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={addAgent}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-smd btn btn-primary"
                  disabled={isLoading}
                >
                  {isLoading ? 'Adding...' : 'Add Agent'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default HumanAgents; 